import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrgPluginsAPI } from '@makyoapp/makyo-sdk'
import { IOAuth2PluginData } from '@makyoapp/makyo-sdk/models/Plugins'
import { PluginAdapter } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, ModalGrid, PluginCard } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'

import OauthComponent from './OauthComponent'

function ErrorOauthGoogle() {
  return (
    <div className='mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-w-[500px] mx-auto'>
      <div className='flex items-start space-x-3 mb-4'>
        <div className='flex-shrink-0'>
          <MaterialIcon icon='warning' className='text-red-600 dark:text-red-400' size={24} />
        </div>
        <div className='flex-1'>
          <h3 className='text-sm font-medium text-red-800 dark:text-red-200 mb-2'>
            Authorization Error: OAuth Permission Required
          </h3>
          <p className='text-sm text-red-700 dark:text-red-300 mb-4'>
            To use this plugin, you need to grant the necessary permissions. Please follow these steps to resolve this
            issue:
          </p>
        </div>
      </div>

      <div className='space-y-4'>
        <div className='bg-white dark:bg-gray-800 p-3 rounded border'>
          <h4 className='font-medium text-sm text-gray-900 dark:text-gray-100 mb-2'>Step 1: Review Permissions</h4>
          <p className='text-xs text-gray-600 dark:text-gray-400'>
            Carefully review all requested permissions to ensure they are necessary for the plugin functionality.
          </p>
        </div>

        <div className='bg-white dark:bg-gray-800 p-3 rounded border'>
          <h4 className='font-medium text-sm text-gray-900 dark:text-gray-100 mb-2'>
            Step 2: Grant All Required Access
          </h4>

          <div className='mt-4 mb-4'>
            <img
              src='https://storage.makyo.me/assets/oauth-info.png'
              alt='OAuth permissions example'
              className='w-full rounded border'
            />
          </div>

          <p className='text-xs text-gray-600 dark:text-gray-400 mb-2'>
            Make sure to check <strong>ALL</strong> the permission boxes, including:
          </p>
          <ul className='text-xs text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1'>
            <li>Basic profile information</li>
            <li>Email address access</li>
            <li>Any service-specific permissions</li>
          </ul>
        </div>

        <div className='bg-white dark:bg-gray-800 p-3 rounded border'>
          <h4 className='font-medium text-sm text-gray-900 dark:text-gray-100 mb-2'>Step 3: Complete Authorization</h4>
          <p className='text-xs text-gray-600 dark:text-gray-400'>
            Click &ldquo;Allow&rdquo; or &ldquo;Authorize&rdquo; to complete the process. If you accidentally denied
            some permissions, try again.
          </p>
        </div>
      </div>

      <div className='mt-4 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded'>
        <p className='text-xs text-blue-700 dark:text-blue-300'>
          <strong>Note:</strong> If you continue to experience issues, try clearing your browser cache or using an
          incognito window.
        </p>
      </div>
    </div>
  )
}

interface PluginOauth2ModalProps {
  /**
   * the current plugin data
   */
  plugin: PluginAdapter
  show: boolean
  onClose: () => void
  onSuccess?: () => void
}

/**
 * PluginOauth2Modal is a component that handles OAuth2 authentication for plugins.
 * It provides a streamlined interface for connecting OAuth2-based services.
 *
 * @param {PluginOauth2ModalProps} props - The properties that define the plugin details.
 * @param {Plugin} props.plugin - The plugin object containing the OAuth2 configuration.
 *
 * @example
 * <PluginOauth2Modal plugin={plugin} show={showModal} onClose={handleClose} />
 *
 * @returns {JSX.Element} A modal containing OAuth2 authentication interface.
 */
export default function PluginOauth2Modal({ plugin, show, onClose, onSuccess }: PluginOauth2ModalProps) {
  const { t } = useTranslation()
  const { currentOrg } = useCurrentOrgContext()
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [tempAuthData, setTempAuthData] = useState<{
    access_token: string
    refresh_token: string
    expires_in: number
    label: string
    email: string
  } | null>(null)

  const handleAddAuth = ({
    access_token,
    refresh_token,
    expires_in,
    label,
    email,
  }: {
    access_token: string
    refresh_token: string
    expires_in: number
    label: string
    email: string
  }) => {
    // Clear any previous errors and store the auth data temporarily
    setError(null)
    setTempAuthData({
      access_token,
      refresh_token,
      expires_in,
      label,
      email,
    })
  }

  const handleAuthError = (errorMessage?: string) => {
    // Reset the modal to initial state and show error
    setTempAuthData(null)
    setError(errorMessage || t('Authentication failed. Please try again.'))
  }

  // Clear error and auth data when modal is closed
  useEffect(() => {
    if (!show) {
      setError(null)
      setTempAuthData(null)
    }
  }, [show])

  const handleSave = () => {
    if (!currentOrg || !tempAuthData) {
      return
    }

    setSaving(true)
    const auth: IOAuth2PluginData = {
      access_token: tempAuthData.access_token,
      refresh_token: tempAuthData.refresh_token,
      expires_in: tempAuthData.expires_in,
    }

    OrgPluginsAPI.ReqAdd(currentOrg.id, {
      // _Label: tempAuthData.label ?? plugin.name,
      _Label: '', //todo remove this and confirm
      _PluginID: plugin.id,
      _Backend: {},
      _Frontend: {},
      _Auth: auth,
    })
      .then(() => {
        toasted(t('Plugin successfully connected'))
        onClose()
        onSuccess?.()
      })
      .catch((e) => {
        console.error(e)
        toasted(t('Error connecting plugin'), { type: 'error' })
      })
      .finally(() => {
        setSaving(false)
        setTempAuthData(null)
      })
  }

  return (
    <ModalGrid
      show={show}
      onCancel={onClose}
      onConfirm={handleSave}
      id={`plugin-oauth2-modal`}
      title={plugin.name}
      onHide={onClose}
      isLoading={saving}
      // @ts-ignore
      noPadding
    >
      <div className='min-h-[400px] overflow-y-auto'>
        {/* Display plugin card with details */}
        <PluginCard
          className='p-8 bg-zinc-100 dark:bg-slate-900 border-b border-zinc-200 dark:border-slate-600'
          isBoxed={false}
          name={plugin.name}
          version={''}
          description={plugin.description}
          icon={<img alt={plugin.name} src={plugin.icon} width={48} height={48} />}
        />

        {/* OAuth Component or Connected State */}
        <div className='p-8'>
          {tempAuthData ? (
            <div className='flex items-center justify-center'>
              <div className='w-full max-w-[500px] p-6 bg-white dark:bg-[#1f2937] rounded-2xl border border-gray-200/60 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-200'>
                <div className='flex items-center gap-5'>
                  <div className='flex-1 min-w-0'>
                    <div className='text-[13px] text-gray-500 dark:text-gray-400 mb-0.5'>Sign in as</div>
                    <div className='font-medium text-[15px] text-gray-900 dark:text-white truncate'>
                      {tempAuthData.label}
                    </div>
                    <div className='text-[14px] text-gray-600 dark:text-gray-400 truncate'>{tempAuthData.email}</div>
                  </div>
                  <img
                    src={plugin.icon || 'https://www.google.com/favicon.ico'}
                    alt={plugin.name}
                    className='w-7 h-7 object-contain'
                  />
                </div>
                <div className='mt-5 flex justify-between items-center'>
                  <span className='text-sm text-muted-foreground'>{t('Click Save to confirm this account')}</span>
                  <Button
                    onClick={() => {
                      setTempAuthData(null)
                      setError(null)
                    }}
                    variant={ButtonVariant.Outlined}
                    className='text-[13px] min-w-[100px] !border-gray-300 dark:!border-gray-600 hover:!border-gray-400 dark:hover:!border-gray-500'
                  >
                    {t('Remove')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <OauthComponent plugin={plugin} onSuccess={handleAddAuth} onError={handleAuthError} />
              {error && (
                <>
                  {error === 'err_auth_insufficient_scopes' ? (
                    <ErrorOauthGoogle />
                  ) : (
                    <div className='mt-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-w-[400px] mx-auto'>
                      <div className='text-sm text-red-700 dark:text-red-400 text-center'>{error}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalGrid>
  )
}
